import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon } from 'lucide-react';
import { SAMPLES } from '../../data/atomizerSamples';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { AtomLedger } from './AtomLedger';
import { useAtomizer } from './useAtomizer';
const MOBILE = '(max-width: 640px)';

/**
 * Desktop: a tab set. The first document is open on arrival, so the results table
 * is always on the page and a click swaps it in place.
 * Mobile: an accordion. The rules open directly under the card that was tapped;
 * tapping it again closes them.
 *
 * Samples only for now. Upload and paste come back once the platform exposes a
 * public extraction endpoint; runText/runFile stay in useAtomizer for that.
 */
export function Atomizer() {
  const {
    result,
    message,
    libOpen,
    setLibOpen,
    send,
    runSample,
    clear,
    submitLibrary
  } = useAtomizer();
  const mobile = useMediaQuery(MOBILE);
  const ledgerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const userPicked = useRef(false);

  // Desktop opens on the first document so there's never an empty space to miss.
  useEffect(() => {
    if (!window.matchMedia(MOBILE).matches) runSample(SAMPLES[0].id);
  }, [runSample]);

  // After a click, bring the answer into view.
  useEffect(() => {
    if (!result || !userPicked.current) return;
    userPicked.current = false;
    const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    if (mobile) {
      // A previously open card above may just have collapsed, so pin the tapped card under the nav.
      cardRefs.current[result.id]?.scrollIntoView({
        block: 'start',
        behavior
      });
      return;
    }
    // Desktop: only nudge when the rules header sits low; keep the cards in sight for switching.
    const top = ledgerRef.current?.getBoundingClientRect().top;
    if (top === undefined) return;
    const want = window.innerHeight - 380;
    if (top > want) window.scrollBy({
      top: top - want,
      behavior
    });
  }, [result, mobile]);
  function pick(id: string) {
    if (result?.id === id) {
      if (mobile) clear();
      return;
    }
    userPicked.current = true;
    runSample(id);
  }
  const ledgerProps = {
    libOpen,
    setLibOpen,
    send,
    submitLibrary
  };
  return <div id="atomizer-field" style={{
    display: 'flex',
    flexDirection: 'column'
  }} data-id="element-82" data-mp="khr862kdli68">
      <div className="picker" data-id="element-83" data-mp="6qw5x94dtzbm">
        <div className="atom-hd" data-id="element-84" data-mp="6pul4cxkdygp">
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6
        }} data-id="element-85" data-mp="bie0nfftb3o5">
            <div className="t" data-id="element-86" data-mp="bkh0er6rob62">See how it works</div>
            <div className="st" data-id="element-87" data-mp="3t0gokba47sed">Pick a document to view how Synthia turns it into instructional data</div>
          </div>
          <div className="eyebrow" style={{
          letterSpacing: '.1em'
        }} data-id="element-88" data-mp="1t46lu1eraxe">
            ATOMIZER
          </div>
        </div>

        <div className="pick-g" data-id="element-89" data-mp="3k0tn5phdymlb">
          {SAMPLES.map(s => {
          const on = result?.id === s.id;
          return <React.Fragment key={s.id} data-id="element-90" data-mp="35b8sg72o7yu6">
                <button ref={el => {
              cardRefs.current[s.id] = el;
            }} type="button" className={`pick${on ? ' on' : ''}`} aria-pressed={mobile ? undefined : on} aria-expanded={mobile ? on : undefined} aria-controls={mobile && on ? `ledger-${s.id}` : undefined} onClick={() => pick(s.id)} data-id="element-91" data-mp="35c0nngscvujx">
                  <span className="bd" data-id="element-92" data-mp="3jai7h3f54mwk">{s.badge}</span>
                  <span className="tx" data-id="element-93" data-mp="1v1yisa25fyl">
                    <span className="nm" data-id="element-94" data-mp="1u09fjw7isxv">{s.name}</span>
                    <span className="mt" data-id="element-95" data-mp="1wuy5nm96zx3">{s.meta}</span>
                  </span>
                  <ChevronDownIcon className="chev" size={18} aria-hidden="true" data-id="element-96" data-mp="3t3ft3iffirze" />
                </button>
                {mobile && on && result && <AtomLedger key={result.id} result={result} inline {...ledgerProps} data-id="element-97" data-mp="5wwzfvo2trbu" />}
              </React.Fragment>;
        })}
        </div>

        <div className="pick-note" data-id="element-98" data-mp="20clk8hqu61ls">
          Want it on your own document?{' '}
          <Link to="/contact" style={{
          textDecoration: 'underline',
          textUnderlineOffset: 3
        }} data-id="element-99" data-mp="25andtwlw1jbi">
            Send us one
          </Link>{' '}
          and we’ll run it.
        </div>
        {message && <div className="atom-msg" role="status" data-id="element-100" data-mp="2e3qojx55fi2g">
            {message}
          </div>}
      </div>

      {!mobile && result && <AtomLedger key={result.id} ref={ledgerRef} result={result} {...ledgerProps} data-id="element-101" data-mp="1hgcc0b2m859p" />}
    </div>;
}